import { Injectable } from '@angular/core';

export const EOD_PERSONNEL = 'EOD_PERSONNEL';
export const FRONT_LINE_REPORTER = 'FRONT_LINE_REPORTER';
export const REGION_ADMIN = 'REGION_ADMIN';
export const ORGANIZATION_ADMIN = 'ORGANIZATION_ADMIN';
export const S2S_ADMIN = 'S2S_ADMIN';

@Injectable({
  providedIn: 'root',
})

export class PermissionService {
  private roles: string[] = [];
  private permissions: string[] = [];
  public dropdownRoles: string[] = [];

  /**
   * A mapping from roles to the permissions they imply.
   * For example, if EOD_PERSONNEL can view and edit EOD.
   */
  private roleToPermissionsMap: Record<string, string[]> = {
    EOD_PERSONNEL: ['view:eod', 'view:hero', 'edit:eod', 'edit:hero'],
    FRONT_LINE_REPORTER: ['view:hero'],
    REGION_ADMIN: [
      'view:users',
      'edit:users',
      'create:organizations',
      'view:organizations',
      'edit:organizations',
      'view:regions',
      'edit:regions',
      'edit:map-layer',
      'delete:map-layer',
      'view:hero',
      'view:eod',
      'edit:hero',
      'edit:eod',
      'delete:hero',
      'delete:eod',
      'export:reports'
    ],
    ORGANIZATION_ADMIN: [
      'view:hero',
      'view:eod',
      'edit:hero',
      'edit:eod',
      'delete:hero',
      'delete:eod',
      'view:users',
      'edit:users',
      'view:organizations',
      'edit:organizations',
      'edit:map-layer',
      'delete:map-layer',
      'export:reports'
    ],
    S2S_ADMIN: [
      'view:users',
      'create:organizations',
      'view:organizations',
      'edit:organizations',
      'view:regions',
      'create:regions',
      'edit:regions',
      'view:hero',
      'edit:hero',
      'view:eod',
      'edit:eod',
      'delete:hero',
      'delete:eod',
      'edit:map-layer',
      'delete:map-layer',
      'export:reports'
    ],
  };

  constructor() {
    this.loadRolesFromLocalStorage();
    this.calculatePermissionsFromRoles();
    this.calculateDropdownRoles();
  }

  /**
   * Reads the user's roles from localStorage
   */
  private loadRolesFromLocalStorage(): void {
    // For example, if localStorage has: ["EOD_PERSONNEL", "HERO_MANAGER"]
    // `localStorage.getItem('ACCOUNT_ROLES')` might return '["EOD_PERSONNEL","HERO_MANAGER"]'
    const stored = localStorage.getItem('ACCOUNT_ROLES');
    console.log('stored', stored);

    if (stored) {
      try {
        // Parse the JSON string into an array of roles
        this.roles = stored.split(',')
      } catch (error) {
        console.error('Failed to parse ACCOUNT_ROLES from localStorage.', error);
        this.roles = [];
      }
    }
  }

  /**
   * Based on the roles we have, derive all unique permissions.
   */
  private calculatePermissionsFromRoles(): void {
    // Flat map each role's permissions
    const allPermissions = this.roles.flatMap((role) => {
      return this.roleToPermissionsMap[role] || [];
    });

    // Remove duplicates by turning it into a set, then back to array
    this.permissions = Array.from(new Set(allPermissions));
  }

  private calculateDropdownRoles(): void {
    const possible = new Set<string>();
    if (this.roles.includes(S2S_ADMIN)) {
      possible.add(S2S_ADMIN);
      possible.add(REGION_ADMIN);
      possible.add(ORGANIZATION_ADMIN);
      possible.add(EOD_PERSONNEL);
      possible.add(FRONT_LINE_REPORTER);
    }
    if (this.roles.includes(REGION_ADMIN)) {
      possible.add(ORGANIZATION_ADMIN);
      possible.add(EOD_PERSONNEL);
      possible.add(FRONT_LINE_REPORTER);
    }
    if (this.roles.includes(ORGANIZATION_ADMIN)) {
      possible.add(EOD_PERSONNEL);
      possible.add(FRONT_LINE_REPORTER);
    }
    this.dropdownRoles = Array.from(possible);
  }


  /**
   * Returns true if the user has the given permission, e.g., "view:eod" or "edit:hero".
   */
  public can(permission: string): boolean {
    return this.permissions.includes(permission);
  }

  public hasRole(role: string): boolean {
    return this.roles.includes(role);
  }

  /**
   * If at any point the user's roles change (login, logout, etc.),
   * re-run the load and permissions mapping.
   */
  public reloadRolesAndPermissions(): void {
    this.loadRolesFromLocalStorage();
    this.calculatePermissionsFromRoles();
    this.calculateDropdownRoles();
  }
}
